import { DatePicker, LocalizationProvider } from "@mui/lab";
import AdapterMoment from "@mui/lab/AdapterMoment";
import {
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  TextField,
  Typography,
} from "@mui/material";
import { ApiError, ApiErrorClass, Plan } from "@readyfit-common/models";
import { handleApiError } from "@readyfit-common/utils";
import moment from "moment";
import { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { LoadingButton } from "src/grading/components";
import RFModal from "src/grading/components/RFModal/RFModal";
import { MY_LIBRARY_ROUTES } from "src/grading/routes/MyLibraryRoutes";
import { useAppDispatch } from "src/store/hooks";
import { showSnackbar } from "src/store/reducers/navigation/navigationSlice";
import {
  useAssignTrainingPlanMutation,
  useGetPlanTemplatesLibraryQuery,
} from "src/store/reducers/training/trainingApiSlice";
import theme from "src/theme";
import TrainingPlanRow from "../TrainingPlanRow/TrainingPlanRow";
import useStyles from "./AssignTrainingModal.styles";

interface AssignTrainingModalProps {
  onClose: () => void;
  open: boolean;
}

const LIMIT = 9999;
const OFFSET = 0;

const AssignTrainingModal = ({ onClose, open }: AssignTrainingModalProps) => {
  const classes = useStyles();
  const { data: planTemplates, isFetching: isLoadingPlanTemplates } =
    useGetPlanTemplatesLibraryQuery({
      limit: LIMIT,
      offset: OFFSET,
    });
  const [selectedPlanTemplate, setSelectedPlanTemplate] = useState<Plan | null>(
    null,
  );
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [assignTrainingPlan, { isLoading: isLoadingAssignTrainingPlan }] =
    useAssignTrainingPlanMutation();
  const params = useParams<{ athleteId: string }>();
  const [startDate, setStartDate] = useState(
    moment().startOf("isoWeek").add(1, "week"),
  );

  const handleSubmit = async () => {
    try {
      if (!selectedPlanTemplate)
        throw new ApiErrorClass({ message: "No plan template selected" });

      const response = await assignTrainingPlan({
        athleteId: params.athleteId,
        planTemplateId: selectedPlanTemplate.id,
        date: startDate.toISOString(),
      }).unwrap();

      if (!response.success) throw new Error(response.message);

      dispatch(
        showSnackbar({
          open: true,
          message: "Plan template assigned successfully",
        }),
      );
    } catch (error: unknown) {
      handleApiError(dispatch, error as ApiError);
    } finally {
      onClose();
    }
  };

  return (
    <RFModal
      open={open}
      boxProps={{ className: classes.container }}
      onClose={onClose}
    >
      <Grid
        container
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit();
        }}
        rowGap={4}
        sx={{ flexDirection: "column", minHeight: "100%" }}
      >
        <Grid container alignItems={"center"} sx={{ alignSelf: "flex-start" }}>
          <Typography variant="h5" fontSize={18}>
            Assign Training
          </Typography>

          <Button
            color="primary"
            variant="contained"
            onClick={() => history.push(MY_LIBRARY_ROUTES.PLAN_TEMPLATES)}
            className={classes.createNewButton}
          >
            Create New
          </Button>
        </Grid>

        <Grid container>
          <InputLabel className={classes.inputLabel}>Start Date</InputLabel>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              value={startDate}
              onChange={(date) => {
                date && setStartDate(date);
              }}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" fullWidth />
              )}
            />
          </LocalizationProvider>
        </Grid>

        <Grid container sx={{ flexGrow: 1 }}>
          {isLoadingPlanTemplates ? (
            <Grid
              container
              justifyContent={"center"}
              alignItems={"center"}
              sx={{ height: "100%" }}
            >
              <CircularProgress />
            </Grid>
          ) : (
            <Grid xs={12}>
              <Grid container rowGap={theme.spacing(1)}>
                {planTemplates?.data.plans.map((planTemplate) => (
                  <TrainingPlanRow
                    key={planTemplate.id}
                    planTemplateWorkout={planTemplate}
                    onSelect={(planTemplate) =>
                      setSelectedPlanTemplate(planTemplate)
                    }
                    selectedPlanTemplate={selectedPlanTemplate}
                  />
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>

        <Grid
          container
          item
          xs={12}
          justifyContent="space-between"
          sx={{ alignSelf: "flex-end" }}
        >
          <Grid item>
            <Button
              color="secondary"
              className={` ${classes.button} ${classes.backButton}`}
              onClick={onClose}
            >
              Cancel
            </Button>
          </Grid>

          <Grid item>
            <LoadingButton
              variant="contained"
              color="primary"
              className={classes.button}
              label={"Assign Training"}
              onClick={handleSubmit}
              loading={isLoadingAssignTrainingPlan}
              disabled={!selectedPlanTemplate}
            />
          </Grid>
        </Grid>
      </Grid>
    </RFModal>
  );
};

export default AssignTrainingModal;
